import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Area to server mapping matching your SOCKET_URLS structure
const AREA_TO_SERVER_MAP = {
  "Barka Sayal": "202.140.131.110:5050",
  "Argada": "202.140.131.118:5050", 
  "North Karanpura": "202.140.131.98:5050",
  "Bokaro & Kargali": "202.140.131.67:5050",
  "Kathara": "202.140.131.90:5050",
  "Giridih": "202.140.131.82:5050",
  "Amrapali & Chandragupta": "202.140.131.74:5050",
  "Rajhara": "202.140.131.102:5050",
  "Kuju": "202.140.131.122:5050",
  "Hazaribagh": "202.140.131.114:5050",
  "Rajrappa": "202.140.131.126:5050",
  "Dhori": "202.140.131.86:5050",
  "Piparwar": "202.140.131.94:5050"
};

// Event type mapping for display
const EVENT_TYPE_MAP = {
  'CD': 'Crowd Detection',
  'VD': 'Vehicle Detection', 
  'PD': 'Person Detection',
  'ID': 'Intrusion Detection',
  'VC': 'Traffic Congestion',
  'LS': 'Loaded Vehicle',
  'US': 'Unloaded Vehicle',
  'CT': 'Camera Tampering',
  'SH': 'Safety Hazard',
  'II': 'Insufficient Illumination',
  'OR': 'Off-Route Alert',
  'TA': 'Tamper Alert',
  'OS': 'Over Speed Alert',
  'USP': 'Unauthorized Stoppage Alert',
};

const MAX_EVENTS_PER_REPORT = 14000;
const IMAGE_FETCH_TIMEOUT = 5000;
const MAX_CONCURRENT_IMAGES = 10;

/**
 * Fetch geofence data for a vehicle alert
 */
const fetchGeofenceData = async (geofenceId) => {
  if (!geofenceId) return null;
  
  try {
    const response = await fetch(`http://192.168.29.244:3000/api/geofences?id=${geofenceId}`);
    if (!response.ok) {
      console.warn(`Failed to fetch geofence ${geofenceId}`);
      return null;
    }
    
    const data = await response.json();
    console.log('📍 Geofence data fetched:', {
      id: geofenceId,
      name: data.name,
      type: data.geotype,
      hasCoordinates: !!data.geojson_coordinates
    });
    
    return data;
  } catch (error) {
    console.error('Error fetching geofence:', error);
    return null;
  }
};

/**
 * Extract coordinates from vehicle alert event
 */
const extractCoordinates = (event) => {
  if (event?.currentLocation?.lat && event?.currentLocation?.lng) {
    return {
      lat: parseFloat(event.currentLocation.lat),
      lng: parseFloat(event.currentLocation.lng)
    };
  }
  
  if (event?.coordinates && typeof event.coordinates === 'string') {
    const coords = event.coordinates.split(',').map(c => parseFloat(c.trim()));
    if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
      return { lat: coords[0], lng: coords[1] };
    }
  }
  
  return null;
};

/**
 * Convert lat/lng to pixel coordinates
 */
const latLngToPixel = (lat, lng, zoom, centerLat, centerLng, width, height) => {
  const scale = Math.pow(2, zoom);
  const worldWidth = 256 * scale;
  
  // Convert to world coordinates
  const worldX = ((lng + 180) / 360) * worldWidth;
  const worldY = ((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2) * worldWidth;
  
  // Convert center to world coordinates
  const centerWorldX = ((centerLng + 180) / 360) * worldWidth;
  const centerWorldY = ((1 - Math.log(Math.tan(centerLat * Math.PI / 180) + 1 / Math.cos(centerLat * Math.PI / 180)) / Math.PI) / 2) * worldWidth;
  
  // Calculate pixel position relative to center
  const pixelX = (worldX - centerWorldX) + (width / 2);
  const pixelY = (worldY - centerWorldY) + (height / 2);
  
  return { x: pixelX, y: pixelY };
};

/**
 * Draw geofence on canvas
 */
const drawGeofence = (ctx, geofenceData, zoom, centerLat, centerLng, width, height) => {
  if (!geofenceData || !geofenceData.geojson_coordinates?.coordinates) {
    console.warn('No geofence coordinates to draw');
    return;
  }
  
  const coords = geofenceData.geojson_coordinates.coordinates;
  const geofenceType = geofenceData.geotype;
  const geofenceColor = geofenceData.attributes?.color || '#FFA500';
  const polylineDistance = geofenceData.attributes?.polylineDistance || 100;
  
  // Extract coordinate array based on GeoJSON structure
  let geofenceCoords = [];
  if (Array.isArray(coords) && coords.length > 0) {
    if (Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
      geofenceCoords = coords[0]; // Polygon outer ring
    } else {
      geofenceCoords = coords; // Direct coordinate array (Path)
    }
  }
  
  if (geofenceCoords.length === 0) {
    console.warn('No valid geofence coordinates found');
    return;
  }
  
  console.log(`🗺️ Drawing ${geofenceType === 'P' ? 'PATH' : 'POLYGON'} geofence with ${geofenceCoords.length} points`);
  
  // Convert all coordinates to pixels
  const pixelCoords = geofenceCoords.map(coord => 
    latLngToPixel(coord[0], coord[1], zoom, centerLat, centerLng, width, height)
  );
  
  if (geofenceType === 'P') {
    // PATH/ROUTE - Draw polyline with buffer corridor
    
    // Draw the main route line
    ctx.strokeStyle = geofenceColor;
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    
    pixelCoords.forEach((point, i) => {
      if (i === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });
    ctx.stroke();
    
    // Draw buffer corridor
    const bufferMeters = polylineDistance;
    const metersPerPixel = (156543.03392 * Math.cos(centerLat * Math.PI / 180)) / Math.pow(2, zoom);
    const bufferPixels = bufferMeters / metersPerPixel;
    
    // Create buffer polygons
    const leftBuffer = [];
    const rightBuffer = [];
    
    for (let i = 0; i < pixelCoords.length; i++) {
      const point = pixelCoords[i];
      const nextPoint = pixelCoords[i + 1];
      
      let angle = 0;
      if (nextPoint) {
        angle = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x);
      } else if (i > 0) {
        const prevPoint = pixelCoords[i - 1];
        angle = Math.atan2(point.y - prevPoint.y, point.x - prevPoint.x);
      }
      
      const perpAngle = angle + Math.PI / 2;
      const offsetX = Math.cos(perpAngle) * bufferPixels;
      const offsetY = Math.sin(perpAngle) * bufferPixels;
      
      leftBuffer.push({ x: point.x + offsetX, y: point.y + offsetY });
      rightBuffer.push({ x: point.x - offsetX, y: point.y - offsetY });
    }
    
    // Draw corridor polygon
    ctx.fillStyle = geofenceColor + '26'; // ~15% opacity
    ctx.strokeStyle = geofenceColor + '80'; // ~50% opacity
    ctx.lineWidth = 1;
    ctx.beginPath();
    
    leftBuffer.forEach((point, i) => {
      if (i === 0) ctx.moveTo(point.x, point.y);
      else ctx.lineTo(point.x, point.y);
    });
    
    rightBuffer.reverse().forEach(point => {
      ctx.lineTo(point.x, point.y);
    });
    
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    console.log('✅ Route corridor drawn with buffer:', bufferMeters, 'meters');
    
  } else {
    // POLYGON AREA - Draw filled polygon
    ctx.fillStyle = geofenceColor + '33'; // ~20% opacity
    ctx.strokeStyle = geofenceColor;
    ctx.lineWidth = 3;
    ctx.beginPath();
    
    pixelCoords.forEach((point, i) => {
      if (i === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });
    
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    console.log('✅ Polygon geofence drawn');
  }
};

/**
 * Generate static map image for vehicle alerts with geofence using canvas
 */
const generateVehicleAlertMapImage = async (event) => {
  try {
    const coordinates = extractCoordinates(event);
    if (!coordinates) {
      console.warn('⚠️ No valid coordinates for vehicle alert map');
      return null;
    }

    const { lat, lng } = coordinates;
    
    // Fetch geofence data if available
    let geofenceData = null;
    if (event.geofenceId) {
      console.log('📍 Fetching geofence data for:', event.geofenceId);
      geofenceData = await fetchGeofenceData(event.geofenceId);
    }
    
    // Determine zoom level based on geofence size or use default
    let zoom = 15;
    if (geofenceData?.geojson_coordinates?.coordinates) {
      const coords = geofenceData.geojson_coordinates.coordinates;
      let allCoords = [];
      
      if (Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
        allCoords = coords[0];
      } else {
        allCoords = coords;
      }
      
      // Calculate bounds
      const lats = allCoords.map(c => c[0]);
      const lngs = allCoords.map(c => c[1]);
      const minLat = Math.min(...lats, lat);
      const maxLat = Math.max(...lats, lat);
      const minLng = Math.min(...lngs, lng);
      const maxLng = Math.max(...lngs, lng);
      
      // Calculate appropriate zoom level
      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);
      
      if (maxDiff > 0.1) zoom = 12;
      else if (maxDiff > 0.05) zoom = 13;
      else if (maxDiff > 0.02) zoom = 14;
      else zoom = 15;
      
      console.log('📏 Calculated zoom level:', zoom, 'for bounds:', { latDiff, lngDiff });
    }
    
    const width = 400;
    const height = 300;

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Calculate tile coordinates
    const tileSize = 256;
    const scale = Math.pow(2, zoom);
    const worldCoordX = ((lng + 180) / 360) * scale;
    const worldCoordY = ((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2) * scale;

    const centerTileX = Math.floor(worldCoordX);
    const centerTileY = Math.floor(worldCoordY);

    // Load tiles around center
    const tilesToLoad = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const tileX = centerTileX + dx;
        const tileY = centerTileY + dy;
        tilesToLoad.push({ x: tileX, y: tileY, dx, dy });
      }
    }

    // Draw background
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, width, height);

    // Load and draw tiles
    const tilePromises = tilesToLoad.map(async ({ x, y, dx, dy }) => {
      try {
        const tileUrl = `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
        const response = await fetch(tileUrl);
        const blob = await response.blob();
        const img = await createImageBitmap(blob);
        
        const offsetX = (width / 2) + (dx * tileSize) - ((worldCoordX - centerTileX) * tileSize);
        const offsetY = (height / 2) + (dy * tileSize) - ((worldCoordY - centerTileY) * tileSize);
        
        ctx.drawImage(img, offsetX, offsetY, tileSize, tileSize);
      } catch (error) {
        console.warn(`⚠️ Failed to load tile ${x},${y}:`, error);
      }
    });

    await Promise.all(tilePromises);

    // Draw geofence if available
    if (geofenceData) {
      console.log('🎨 Drawing geofence on map');
      drawGeofence(ctx, geofenceData, zoom, lat, lng, width, height);
    }

    // Draw vehicle marker at center (on top of geofence)
    const markerX = width / 2;
    const markerY = height / 2;
    
    // Marker shadow
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.beginPath();
    ctx.ellipse(markerX, markerY + 5, 8, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Marker pin
    ctx.fillStyle = event.alertType === 'tamper' ? '#DC143C' : '#FF6B35';
    ctx.beginPath();
    ctx.arc(markerX, markerY - 10, 10, 0, Math.PI * 2);
    ctx.fill();
    
    // White border for marker
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Add alert icon
    ctx.fillStyle = 'white';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(event.alertType === 'tamper' ? '⚠' : '🚛', markerX, markerY - 10);

    // Add coordinates label at bottom
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(0, height - 25, width, 25);
    ctx.fillStyle = 'white';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`${lat.toFixed(5)}, ${lng.toFixed(5)}`, width / 2, height - 12);
    
    // Add geofence indicator if present
    if (geofenceData) {
      const geofenceLabel = geofenceData.geotype === 'P' ? '📍 Route' : '🗺️ Area';
      ctx.fillStyle = geofenceData.attributes?.color || '#FFA500';
      ctx.fillRect(width - 70, 10, 60, 20);
      ctx.fillStyle = 'white';
      ctx.font = 'bold 10px Arial';
      ctx.fillText(geofenceLabel, width - 40, 20);
    }

    // Convert canvas to base64
    const base64Image = canvas.toDataURL('image/png');
    console.log('✅ Generated map image with geofence for:', event.vehicleNumber);
    return base64Image;

  } catch (error) {
    console.error('❌ Error generating vehicle alert map:', error);
    return null;
  }
};

// ... (rest of the helper functions remain the same: parseRemarks, normalizeSeverity, etc.)

const parseRemarks = (remarks) => {
  if (!remarks) return '';
  
  try {
    let remarksData = remarks;
    
    if (typeof remarks === 'string') {
      try {
        remarksData = JSON.parse(remarks);
      } catch (e) {
        return remarks;
      }
    }
    
    if (typeof remarksData === 'object' && remarksData.comment) {
      const commentStr = remarksData.comment;
      
      if (commentStr.startsWith('REMARKS_HISTORY:')) {
        try {
          const historyData = commentStr.replace('REMARKS_HISTORY:', '');
          const parsed = JSON.parse(historyData);
          
          if (parsed && Array.isArray(parsed.comments)) {
            const userComments = parsed.comments
              .map((comment) => {
                const commentText = comment.comment || '';
                
                if (commentText.startsWith('EVENT_CLOSED_BY_USER:')) {
                  try {
                    const closedData = JSON.parse(commentText.replace('EVENT_CLOSED_BY_USER:', ''));
                    const timestamp = comment.timestamp ? 
                      new Date(comment.timestamp).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                        timeZone: 'Asia/Kolkata'
                      }) : '';
                    
                    return `${comment.user}: Event closed - ${closedData.finalComment || 'No reason provided'}${timestamp ? ` (${timestamp})` : ''}`;
                  } catch (e) {
                    return `${comment.user}: Event closed${comment.timestamp ? ` (${new Date(comment.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })})` : ''}`;
                  }
                } else if (commentText.startsWith('EVENT_REOPENED_BY_USER:')) {
                  try {
                    const reopenedData = JSON.parse(commentText.replace('EVENT_REOPENED_BY_USER:', ''));
                    const timestamp = comment.timestamp ? 
                      new Date(comment.timestamp).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                        timeZone: 'Asia/Kolkata'
                      }) : '';
                    
                    return `${comment.user}: Event reopened - ${reopenedData.reason || 'No reason provided'}${timestamp ? ` (${timestamp})` : ''}`;
                  } catch (e) {
                    return `${comment.user}: Event reopened${comment.timestamp ? ` (${new Date(comment.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })})` : ''}`;
                  }
                } else {
                  const timestamp = comment.timestamp ? 
                    new Date(comment.timestamp).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                      timeZone: 'Asia/Kolkata'
                    }) : '';
                  
                  return `${comment.user}: ${commentText}${timestamp ? ` (${timestamp})` : ''}`;
                }
              })
              .filter(comment => comment && comment.trim() !== '');
            
            return userComments.join('\n\n');
          }
        } catch (historyError) {
          console.error('❌ Error parsing REMARKS_HISTORY:', historyError);
          return 'Comments parsing error';
        }
      } else {
        const timestamp = remarksData.timestamp ? 
          new Date(remarksData.timestamp).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'Asia/Kolkata'
          }) : '';
        
        return `${remarksData.user || 'User'}: ${commentStr}${timestamp ? ` (${timestamp})` : ''}`;
      }
    }
    
    if (typeof remarksData === 'object') {
      return JSON.stringify(remarksData, null, 2);
    }
    
    return '';
  } catch (error) {
    console.error(`❌ Error parsing remarks:`, error);
    return 'Comments format error';
  }
};

const normalizeSeverity = (severity) => {
  if (!severity || severity === null || severity === undefined) {
    return "Unassigned";
  }
  
  const severityStr = String(severity).toLowerCase().trim();
  
  switch (severityStr) {
    case 'high':
    case 'h':
    case '3':
    case 'urgent':
    case 'critical':
      return "High";
    case 'medium':
    case 'moderate':
    case 'm':
    case '2':
    case 'normal':
      return "Moderate";
    case 'low':
    case 'l':
    case '1':
    case 'minor':
      return "Low";
    case 'unassigned':
    case 'none':
    case 'null':
    case '0':
    case '':
      return "Unassigned";
    default:
      if (['High', 'Moderate', 'Low', 'Unassigned'].includes(severity)) {
        return severity;
      }
      return "Unassigned";
  }
};

// ... (rest of the file remains the same: fetchImageAsBase64, getServerUrlForArea, etc.)

/**
 * Fetch image as base64 with better error handling and CORS support
 */
const fetchImageAsBase64 = async (imageUrl, timeout = IMAGE_FETCH_TIMEOUT) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    const response = await fetch(imageUrl, {
      method: 'GET',
      signal: controller.signal,
      mode: 'cors',
      headers: {
        'Accept': 'image/*',
        'Cache-Control': 'no-cache'
      }
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      console.warn(`⚠️ Image fetch failed with status ${response.status}: ${imageUrl}`);
      return null;
    }
    
    const blob = await response.blob();
    
    if (!blob.type.startsWith('image/')) {
      console.warn(`⚠️ Response is not an image: ${blob.type} for URL: ${imageUrl}`);
      return null;
    }
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to convert blob to base64'));
      reader.readAsDataURL(blob);
    });
    
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn(`⚠️ Image fetch timeout: ${imageUrl}`);
    } else {
      console.warn(`⚠️ Failed to fetch image ${imageUrl}:`, error.message);
    }
    return null;
  }
};

const getServerUrlForArea = (area) => {
  if (AREA_TO_SERVER_MAP[area]) {
    return `https://${AREA_TO_SERVER_MAP[area]}`;
  }
  
  const normalizedArea = area.toLowerCase().trim();
  for (const [mappedArea, server] of Object.entries(AREA_TO_SERVER_MAP)) {
    const normalizedMapped = mappedArea.toLowerCase();
    
    if (normalizedMapped.includes(normalizedArea) || 
        normalizedArea.includes(normalizedMapped)) {
      return `https://${server}`;
    }
  }
  
  console.warn(`⚠️ No server mapping found for area: ${area}`);
  return null;
};

const generateImageUrl = (event) => {
  let serverUrl = null;
  let eventId = null;

  if (event.httpsUrl) {
    serverUrl = event.httpsUrl;
  } else {
    const eventArea = event.area || 
                     event.attributes?.area || 
                     event.location ||
                     event.attributes?.location ||
                     'Unknown';
    
    serverUrl = getServerUrlForArea(eventArea);
  }

  eventId = event.eventId || 
           event.id || 
           event.eventID ||
           event.event_id;

  if (!serverUrl || !eventId) {
    console.warn(`⚠️ Cannot generate image URL:`, {
      area: event.area,
      serverUrl,
      eventId,
      httpsUrl: event.httpsUrl
    });
    return null;
  }
  
  const imageUrl = `${serverUrl}/api/va/event?id=${eventId}`;
  return imageUrl;
};

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata'
    });
  } catch (error) {
    console.warn(`⚠️ Date formatting error for ${dateString}:`, error);
    return dateString || 'Unknown';
  }
};

const processEventsInBatches = async (events, batchSize = MAX_CONCURRENT_IMAGES, onProgress = null) => {
  const processedEvents = [];
  const totalEvents = events.length;
  
  for (let i = 0; i < totalEvents; i += batchSize) {
    const batch = events.slice(i, i + batchSize);
    
    const batchPromises = batch.map(async (event) => {
      try {
        const eventArea = event.area || event.attributes?.area || 'Unknown';
        const eventLocation = event.location || event.attributes?.location || 'Unknown';
        const eventTime = formatDate(event.timestamp || event.eventTime || event.createdAt);
        const eventType = EVENT_TYPE_MAP[event.detectionType || event.eventType] || 
                         (event.detectionType || event.eventType || 'Unknown');
        
        const severity = normalizeSeverity(event.severity);
        const comments = parseRemarks(event.remarks);
        
        let imageBase64 = null;
        let imageUrl = null;
        
        // Handle vehicle alerts - generate map with geofence
        if (event.isVehicleAlert && (event.detectionType === 'OR' || event.detectionType === 'TA') ) {
          console.log('🗺️ Generating map with geofence for vehicle alert:', event.vehicleNumber);
          imageBase64 = await generateVehicleAlertMapImage(event);
          imageUrl = null;
        } else {
          // Regular camera events
          imageUrl = generateImageUrl(event);
          if (imageUrl) {
            imageBase64 = await fetchImageAsBase64(imageUrl);
          }
        }
        
        return {
          area: eventArea,
          location: eventLocation,
          eventTime: eventTime,
          eventType: eventType,
          severity: severity,
          comments: comments,
          eventId: event.eventId || event.id,
          cameraIp: event.cameraIp || event.vehicleNumber || 'Unknown',
          imageBase64: imageBase64,
          imageUrl: imageUrl,
          hasImage: !!imageBase64,
          detectionType: event.detectionType || event.eventType,
          isVehicleAlert: event.isVehicleAlert || false,
          vehicleNumber: event.vehicleNumber,
          originalEvent: event
        };
        
      } catch (error) {
        console.error(`❌ Error processing event ${event.eventId || event.id}:`, error);
        return {
          area: event.area || 'Unknown',
          location: event.location || 'Unknown',
          eventTime: formatDate(event.timestamp || event.eventTime || event.createdAt),
          eventType: EVENT_TYPE_MAP[event.detectionType || event.eventType] || 'Unknown',
          severity: normalizeSeverity(event.severity),
          comments: parseRemarks(event.remarks),
          eventId: event.eventId || event.id,
          cameraIp: event.cameraIp || event.vehicleNumber || 'Unknown',
          imageBase64: null,
          imageUrl: null,
          hasImage: false,
          detectionType: event.detectionType || event.eventType,
          isVehicleAlert: event.isVehicleAlert || false,
          vehicleNumber: event.vehicleNumber,
          originalEvent: event
        };
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    processedEvents.push(...batchResults);
    
    if (onProgress) {
      const processed = Math.min(i + batchSize, totalEvents);
      onProgress({
        stage: 'fetching_images',
        processed: processed,
        total: totalEvents,
        percentage: Math.round((processed / totalEvents) * 100),
        message: `Processing events ${processed}/${totalEvents}`
      });
    }
  }
  
  return processedEvents;
};

/**
 * Process report data and fetch images
 */
const processReportDataForPDF = async (reportData, selectedAreas, onProgress = null) => {
  let allEvents = [];
  
  Object.values(reportData).forEach(events => {
    if (Array.isArray(events)) {
      allEvents.push(...events);
    }
  });
  
  if (allEvents.length === 0) {
    throw new Error('No events found in report data');
  }
  
  if (allEvents.length > MAX_EVENTS_PER_REPORT) {
    console.log(`⚠️ Limiting events to ${MAX_EVENTS_PER_REPORT} (from ${allEvents.length}) for performance`);
    allEvents = allEvents.slice(0, MAX_EVENTS_PER_REPORT);
  }
  
  console.log(`📊 Processing ${allEvents.length} events for PDF generation...`);
  
  const processedData = await processEventsInBatches(allEvents, MAX_CONCURRENT_IMAGES, onProgress);
  
  const imagesFound = processedData.filter(e => e.hasImage).length;
  console.log(`✅ Processed ${processedData.length} events with ${imagesFound} images successfully fetched`);
  
  return processedData;
};

/**
 * Generate PDF report with inline images, severity, and comments in table format
 */
export const generateEnhancedPDF = async (reportData, timeFilter, selectedAreas, onProgress = null) => {
  try {
    console.log('🚀 Starting enhanced PDF generation with geofence support...');
    
    if (onProgress) {
      onProgress({ 
        stage: 'processing', 
        percentage: 0, 
        message: 'Starting PDF generation...' 
      });
    }
    
    // Process data and fetch images
    const processedData = await processReportDataForPDF(reportData, selectedAreas, onProgress);
    
    if (processedData.length === 0) {
      throw new Error('No valid events found to generate PDF');
    }
    
    if (onProgress) {
      onProgress({ 
        stage: 'generating_pdf', 
        percentage: 90, 
        message: 'Creating PDF document...' 
      });
    }
    
    // Create PDF in landscape mode for better table layout with more columns
    const pdf = new jsPDF('l', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    
    // Add header
    pdf.setFontSize(18);
    pdf.setTextColor(25, 25, 112);
    pdf.text('ICCC VA Event Report', pageWidth / 2, 20, { align: 'center' });
    
    // Add report metadata
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    const fromDate = formatDate(timeFilter.startTime);
    const toDate = formatDate(timeFilter.endTime);
    const areaText = Array.isArray(selectedAreas) && selectedAreas.length > 0 
      ? (selectedAreas.includes('All') ? 'All Areas' : selectedAreas.join(', '))
      : 'All Areas';
    
    pdf.text(`From: ${fromDate} To: ${toDate}`, pageWidth / 2, 30, { align: 'center' });
    pdf.text(`Area: ${areaText} | Total Records: ${processedData.length}`, pageWidth / 2, 37, { align: 'center' });
    
    // Calculate row heights dynamically based on content with generous padding for comments
    const getRowHeight = (event) => {
      let commentsHeight = 35;
      
      if (event.comments && event.comments.trim() !== '') {
        const estimatedLines = Math.max(1, Math.ceil(event.comments.length / 60));
        commentsHeight = Math.max(35, estimatedLines * 6 + 10);
      }
      
      const imageHeight = event.hasImage ? 40 : 35;
      return Math.max(commentsHeight, imageHeight, 35);
    };
    
    // Create table with manual row drawing for better control
    const tableHeaders = ['Area', 'Location Name', 'Event Time', 'Event Type', 'Severity', 'Comments', 'Image'];
    
    // Column widths for landscape A4 (297mm width)
    const colWidths = [22, 40, 28, 25, 16, 90, 50];
    const startY = 50;
    let currentY = startY;
    
    console.log('📋 Creating table with headers:', tableHeaders);
    
    // Draw header row with proper background and text colors
    const drawTableHeader = (yPosition) => {
      pdf.setFillColor(31, 81, 142);
      pdf.rect(margin, yPosition, colWidths.reduce((a, b) => a + b, 0), 12, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'bold');
      
      let xPos = margin;
      for (let i = 0; i < tableHeaders.length; i++) {
        pdf.setDrawColor(31, 81, 142);
        pdf.setLineWidth(0.5);
        pdf.rect(xPos, yPosition, colWidths[i], 12);
        
        pdf.text(tableHeaders[i], xPos + colWidths[i]/2, yPosition + 8, { align: 'center' });
        xPos += colWidths[i];
      }
    };
    
    // Draw initial header
    drawTableHeader(currentY);
    currentY += 12;
    
    // Reset styles for data rows
    pdf.setTextColor(0, 0, 0);
    pdf.setFont(undefined, 'normal');
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    
    // Draw data rows
    processedData.forEach((event, index) => {
      const rowHeight = getRowHeight(event);
      
      // Check if we need a new page
      if (currentY + rowHeight > pageHeight - 15) {
        pdf.addPage();
        currentY = 20;
        
        drawTableHeader(currentY);
        currentY += 12;
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'normal');
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
      }
      
      // Alternate row colors
      if (index % 2 === 0) {
        pdf.setFillColor(248, 248, 248);
        pdf.rect(margin, currentY, colWidths.reduce((a, b) => a + b, 0), rowHeight, 'F');
      }
      
      // Draw cell borders and content
      let xPos = margin;
      
      // Area
      pdf.rect(xPos, currentY, colWidths[0], rowHeight);
      pdf.setFontSize(8);
      const areaLines = pdf.splitTextToSize(event.area, colWidths[0] - 2);
      pdf.text(areaLines, xPos + 1, currentY + 6);
      xPos += colWidths[0];
      
      // Location Name
      pdf.rect(xPos, currentY, colWidths[1], rowHeight);
      const locationLines = pdf.splitTextToSize(event.location, colWidths[1] - 2);
      pdf.text(locationLines, xPos + 1, currentY + 6);
      xPos += colWidths[1];
      
      // Event Time
      pdf.rect(xPos, currentY, colWidths[2], rowHeight);
      pdf.setFontSize(7);
      const timeLines = pdf.splitTextToSize(event.eventTime, colWidths[2] - 2);
      pdf.text(timeLines, xPos + 1, currentY + 6);
      xPos += colWidths[2];
      
      // Event Type
      pdf.rect(xPos, currentY, colWidths[3], rowHeight);
      pdf.setFontSize(7);
      const typeLines = pdf.splitTextToSize(event.eventType, colWidths[3] - 2);
      pdf.text(typeLines, xPos + 1, currentY + 6);
      xPos += colWidths[3];
      
      // Severity (with color coding)
      pdf.rect(xPos, currentY, colWidths[4], rowHeight);
      pdf.setFontSize(7);
      
      switch(event.severity) {
        case 'High':
          pdf.setTextColor(220, 20, 60);
          break;
        case 'Moderate':
          pdf.setTextColor(255, 140, 0);
          break;
        case 'Low':
          pdf.setTextColor(34, 139, 34);
          break;
        default:
          pdf.setTextColor(128, 128, 128);
      }
      
      pdf.text(event.severity, xPos + colWidths[4]/2, currentY + rowHeight/2, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      xPos += colWidths[4];
      
      // Comments
      pdf.rect(xPos, currentY, colWidths[5], rowHeight);
      pdf.setFontSize(6);
      
      if (event.comments && event.comments.trim() !== '') {
        const commentLines = pdf.splitTextToSize(event.comments, colWidths[5] - 4);
        
        let textY = currentY + 4;
        const lineHeight = 3;
        
        commentLines.forEach((line) => {
          if (textY + lineHeight <= currentY + rowHeight - 2) {
            pdf.text(line, xPos + 2, textY);
            textY += lineHeight;
          }
        });
        
        if (commentLines.length * lineHeight > rowHeight - 8) {
          pdf.setFontSize(5);
          pdf.setTextColor(128, 128, 128);
          pdf.text('(continued...)', xPos + colWidths[5] - 15, currentY + rowHeight - 3);
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(6);
        }
      } else {
        pdf.setTextColor(128, 128, 128);
        pdf.text('No comments', xPos + 2, currentY + rowHeight/2);
        pdf.setTextColor(0, 0, 0);
      }
      xPos += colWidths[5];
      
      // Image
      pdf.rect(xPos, currentY, colWidths[6], rowHeight);
      if (event.hasImage && event.imageBase64) {
        try {
          const imgPadding = 2;
          const imgWidth = colWidths[6] - (imgPadding * 2);
          const imgHeight = rowHeight - (imgPadding * 2);
          pdf.addImage(event.imageBase64, 'JPEG', xPos + imgPadding, currentY + imgPadding, imgWidth, imgHeight);
        } catch (imgError) {
          console.error(`❌ Error adding image for event ${event.eventId}:`, imgError);
          pdf.setFontSize(6);
          pdf.setTextColor(220, 20, 60);
          pdf.text('Image Error', xPos + 2, currentY + rowHeight/2);
          pdf.setTextColor(0, 0, 0);
        }
      } else {
        pdf.setFontSize(6);
        pdf.setTextColor(128, 128, 128);
        pdf.text('No Image', xPos + 2, currentY + rowHeight/2);
        pdf.setTextColor(0, 0, 0);
      }
      
      currentY += rowHeight;
      pdf.setFontSize(8);
    });
    
    // Add page numbers
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 10);
    }
    
    // Final progress update
    if (onProgress) {
      onProgress({ 
        stage: 'complete', 
        percentage: 100, 
        message: 'PDF generated successfully!' 
      });
    }
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    const filename = `ICCC_VA_Report_Enhanced_${timestamp}.pdf`;
    
    // Download PDF
    pdf.save(filename);
    
    const imagesFound = processedData.filter(e => e.hasImage).length;
    const eventsWithComments = processedData.filter(e => e.comments && e.comments.trim() !== '').length;
    
    console.log(`✅ Enhanced PDF report generated successfully: ${filename}`);
    console.log(`📊 Report Statistics:`, {
      totalEvents: processedData.length,
      eventsWithImages: imagesFound,
      eventsWithComments: eventsWithComments,
      imageSuccessRate: `${Math.round((imagesFound / processedData.length) * 100)}%`,
      commentSuccessRate: `${Math.round((eventsWithComments / processedData.length) * 100)}%`
    });
    
    return { 
      success: true, 
      filename, 
      totalEvents: processedData.length,
      totalImages: imagesFound,
      totalComments: eventsWithComments,
      imageSuccessRate: Math.round((imagesFound / processedData.length) * 100),
      commentSuccessRate: Math.round((eventsWithComments / processedData.length) * 100)
    };
    
  } catch (error) {
    console.error('❌ PDF generation failed:', error);
    
    if (onProgress) {
      onProgress({ 
        stage: 'error', 
        percentage: 0, 
        message: `Error: ${error.message}` 
      });
    }
    
    throw error;
  }
}